import { Subscription, interval } from 'rxjs';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { NavigationService } from '../../../../core/services/navigation.service';
import { StorageService } from '../../../../core/services/storage.service';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { SyncConfigService } from 'src/app/core/services/sync-config.service';
import { CloudProviderTypes, Entities } from 'src/app/core/models/globals';
import { Deployment } from 'src/app/core/models/deployment';
import { DeploymentService } from 'src/app/core/services/deployment.service';
import { ConfigEntityService } from '../../../../core/services/config-entity.service';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDeploymentComponent } from 'src/app/modules/deployment/modals/delete-deployment/delete-deployment.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NewStorageComponent } from '../../modals/new-storage/new-storage.component';
import { JobOutputDialogComponent } from 'src/app/modules/job/modals/jobActions/job-output-dialog/job-output-dialog.component';

@Component({
  selector: 'list-storage',
  templateUrl: './storage-list.component.html',
  styleUrls: ['./storage-list.component.scss'],
})
export class StorageListComponent implements OnInit, OnDestroy {

  deployments: Deployment[];
  dataReloadSubscription: Subscription;
  dataEmpty: boolean = false;
  currentEntity;
  universalSub: Subscription;
  storageDeploymentListSub: Subscription;
  p: number = 1;
  isUserGlobalAdmin: boolean = this.authenticationService.isUserGlobalAdmin;
  cloudTypes = [];
  filterDeployment: string | null = null;
  _dialog = inject(MatDialog)
  _snackBar = inject(MatSnackBar)

  constructor(
    private spinner: NgxSpinnerService,
    private router: Router,
    public storageService: StorageService,
    private route: ActivatedRoute,
    private data: NavigationService,
    private navService: NavigationService,
    private syncConfig: SyncConfigService,
    private depService: DeploymentService,
    private configEntityService: ConfigEntityService,
    private authenticationService: AuthenticationService,
  ) { }

  ngOnInit() {
    this.spinner.show("storageList");
    this.data.sendChildData('');
    this.universalSub = this.navService.breadcrumbEntity.subscribe(x => {
      if (this.currentEntity != x) {
        this.currentEntity = x;
      }
    })
    if (this.currentEntity != Entities.Configurator) {
      this.navService.subNavData(Entities.Storages);
    }
    if (this.currentEntity == Entities.Configurator) {
      //this.navService.sendLeftPanelIndex(5);
      this.configEntityService.sendActiveEntity('Storage')
      this.navService.sendChildData('Storage')
      this.navService.sendParentData('')
    }
    this.cloudTypes = Object.entries(CloudProviderTypes)
      .filter(([key, value]) => isNaN(Number(key)) && !key.includes(":") && key.includes("Google"))
      .map(([key, value]) => ({ name: key, value: value }));
    this.route.queryParams.subscribe(params => this.filterDeployment = params.deployment);
    this.loadStorageList()
    this.dataReloadSubscription = interval(this.syncConfig.config.deployments.listReqInterval).subscribe((x) => {
      this.loadStorageList();
    });
  }

  loadStorageList() {
    if (this.storageDeploymentListSub) this.storageDeploymentListSub.unsubscribe();
    this.deployments = this.depService.getProfileInfoesFromSubject()?.filter(x => x.status == 'Active' && !x.isVdi);
    if (this.deployments) {
      this.manageData(this.deployments)
      return;
    }
    this.getStorageData();
  }

  getStorageData() {
    this.storageDeploymentListSub = this.depService.getAllDeploymentsForUser().subscribe({
      next: (data) => {
        this.manageData(data);
      },
      error: () => {
        this.dataEmpty = true;
        this.spinner.hide("storageList");
      },
      complete: () => { }
    });
  }

  manageData(data) {
    if (data == null) {
      this.spinner.hide("storageList")
      this.dataEmpty = true;
    } else {
      this.deployments = <Deployment[]>JSON.parse(JSON.stringify(data))?.filter(x => x.status == 'Active' && !x.isVdi);
      this.deployments.forEach(x => {
        if (x.storageAccountName && x.storageAccountName.split(",").length > 1) {
          x.storageAccountName.split(",").forEach((y, i) => {
            if (i != 0) {
              this.deployments.push({ ...x, storageAccountName: y, toShowStorageActions: x.cloud.trim() != 'Amazon Web Services'.trim() })
            }
          })
          x.storageAccountName = x.storageAccountName.split(",")[0]
        }
      })
      if (this.filterDeployment) {
        this.deployments = this.deployments.filter(x => x.name == this.filterDeployment)
      }
      this.deployments.sort((a, b) => a.cloud.localeCompare(b.cloud))
      if (this.deployments.length == 0) {
        this.dataEmpty = true;
        this.spinner.hide("storageList");
      } else {
        this.dataEmpty = false;
        if (!this.deployments[1]) {
          this.redirectTo(this.deployments[0]);
        }
        this.spinner.hide("storageList");
      }
    }
  }

  ngOnDestroy() {
    this.dataReloadSubscription?.unsubscribe();
    this.universalSub?.unsubscribe()
    this.storageDeploymentListSub?.unsubscribe();
  }

  redirectTo(deployment: Deployment) {
    if (this.currentEntity == Entities.Configurator) {
      this.router.navigate([deployment.cloud, deployment.name, deployment.storageAccountName, 'settings'], { relativeTo: this.route });
      this.navService.sendParentData(deployment.name)
    } else {
      if (deployment.toShowStorageActions) {
        this.router.navigate([deployment.cloud, deployment.name, deployment.storageAccountName], {
          relativeTo: this.route,
          queryParams: { fileShare: deployment.storageAccountName }
        });
      } else {
        this.router.navigate([deployment.cloud, deployment.name, deployment.storageAccountName], {
          relativeTo: this.route
        });
      }
      this.data.sendChildData(deployment.name);
      this.data.sendDeploymentName(deployment.name);
      this.navService.sendCloud(deployment.cloud);
    }
  }

  newStorage(type: any) {
    const deps = this.deployments.filter(x => x.cloud == type.name).map(x => x.name);
    if (deps.length == 0) {
      this._snackBar.open('No deployments found for this cloud provider.', 'Ok', { duration: 3000 })
      return;
    }
    const dialogRef = this._dialog.open(NewStorageComponent, {
      width: '50%',
      autoFocus: true,
      disableClose: true,
      data: { toAdd: true, type, storageName: '', deploymentNames: deps },
    })
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.spinner.show("storageList");
        this.getStorageData();
      }
    })
  }

  editStorage(deployment: any) {
    const dialogRef = this._dialog.open(NewStorageComponent, {
      width: '50%',
      autoFocus: true,
      disableClose: true,
      data: { toAdd: false, type: { name: deployment.cloud, value: -1 }, storageName: deployment.storageAccountName },
    })
    dialogRef.afterClosed().subscribe(async res => {
      if (res) {
        this.spinner.show("storageList");
        this._snackBar.open('Storage Updated Successfully.', 'Ok', { duration: 3000 })
        this.getStorageData();
      }
    })
  }

  deleteStorage(deployment: Deployment) {
    const dialogRef = this._dialog.open(DeleteDeploymentComponent, {
      width: '30%',
      autoFocus: true,
      disableClose: true,
      data: { name: deployment.storageAccountName, type: 'storage' },
    })
    dialogRef.afterClosed().subscribe(async res => {
      if (res) {
        this.storageService.deleteStorage(deployment.storageAccountName).subscribe({
          next: (data) => {
            this.getStorageData();
            this._snackBar.open('Storage Deleted Successfully.', 'Ok', { duration: 3000 })
          },
          error: (error) => {
            this._snackBar.open(error?.error?.message || 'Failed to delete storage.', 'Ok', { duration: 5000 })
          }
        })
      }
    })
  }

  checkDepStatus(deployment: Deployment) {
    this._dialog.open(JobOutputDialogComponent, {
      width: '90%',
      autoFocus: false,
      disableClose: true,
      hasBackdrop: false,
      id: "jobOutputDialogJobs",
      data: { depName: deployment.name, depId: deployment.id, path: "", id: "jobOutputDialogJobs" },
      panelClass: 'custom-output-dialog-container'
    });
  }
}
